/**
 * Folder 52's endpoints, said once for the server that answers them and the
 * popups and pages that call them — `docs/job-stages.md` §9.7, Board 3.
 *
 * Requests are zod schemas (the server parses with them); answers are types.
 * Money is a decimal string, never a float; days are `YYYY-MM-DD` in the
 * branch's zone.
 */
import { z } from 'zod';
import { DECLINE_REASONS } from './proposal.js';
// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------
/** Board 3 B's *How it went out* chips: *Handed over · Our own email · Printed · Other*. */
export const MARKED_HOW = ['handed_over', 'own_email', 'printed', 'other'];
/** `POST /quotes/:id/mark-quoted` — stored as `sent_how = 'marked'`, `sent_note = how`. */
export const markQuotedSchema = z.object({
    how: z.enum(MARKED_HOW).nullish(),
});
/** `POST /quotes/:id/withdraw` — *Why*, required (Board 3 D); kept in `status_reason`. */
export const withdrawQuoteSchema = z.object({
    reason: z.string().trim().min(1).max(500),
});
/** `POST /quotes/:id/decline` (office) — one of the link's five reasons, and what was said (Board 3 E). */
export const officeDeclineSchema = z.object({
    reason: z.enum(DECLINE_REASONS.map((one) => one.value)),
    note: z.string().trim().max(1000).nullish(),
});
/** `POST /jobs/:id/cancel` — today's reason popup's *Why* (Board 3 F). */
export const cancelJobSchema = z.object({
    reason: z.string().trim().min(1).max(500),
});
/** Board 3 A's *How*: *In person · Phone · Text · Email*. `link` is the portal's own (§9.6). */
export const OFFICE_APPROVAL_HOW = ['in_person', 'phone', 'text', 'email'];
/**
 * The office's `POST /quotes/:id/approve` (Board 3 A): today's selection,
 * plus who (a contact of the property, or a typed name), when, how, what was
 * said, proof and the email box. Who and when are always required; the rest
 * as the company says (`approval_require_*`), checked again on the server
 * (`422 {field}`).
 */
export const officeApprovalSchema = z.object({
    chooseSelections: z.record(z.string().uuid(), z.string().uuid().nullable()).default({}),
    optionalAdds: z.array(z.string().uuid()).default([]),
    serviceAdds: z.array(z.string().uuid()).default([]),
    lineAdds: z.array(z.string().uuid()).default([]),
    /** v1 quotes only: the option taken and its add-ons (the old builder's shape). */
    quoteOptionId: z.string().uuid().nullish(),
    addOnLineIds: z.array(z.string().uuid()).optional(),
    approvedByContactId: z.string().uuid().nullish(),
    signatureName: z.string().trim().max(160).nullish(),
    approvedAt: z.string().datetime({ offset: true }).nullish(),
    how: z.enum(OFFICE_APPROVAL_HOW).nullish(),
    note: z.string().trim().max(4000).nullish(),
    proofFileId: z.string().uuid().nullish(),
    emailCopy: z.boolean().default(true),
});
//# sourceMappingURL=job-stages-api.js.map